import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useCombobox } from 'downshift';
import { Fragment } from 'preact/compat';
import { Option } from 'interfaces/GlobalConfig.interface';

import { ReactComponent as ChevronDownIcon } from 'icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from 'icons/chevron-up.svg';
import {
  ComboboxContainer,
  ComboboxInput,
  ComboboxToggleButton,
  ComboboxMenu,
  ComboboxItem,
  ComboboxNoResults,
  ComboboxHelperText,
  Container,
  ComboboxWrapper,
} from './Combobox.styled';

interface ComboboxV2Props
  extends Omit<React.HTMLProps<HTMLDivElement>, 'onSelect'> {
  options: Option[];
  value?: string;
  placeholder?: string;
  hasError?: boolean;
  helpText?: string;
  onSelect?: (value: string) => void;
}

export const Combobox = ({
  options = [],
  value = '',
  hasError = false,
  helpText,
  onSelect,
  ...props
}: ComboboxV2Props) => {
  const [inputItems, setInputItems] = useState(options);
  const [selectedItem, setSelectedItem] = useState(null);
  const hasUserTyped = useRef(false);

  // Filter function for search
  const getFilteredItems = (inputValue: string) => {
    if (!inputValue) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.value.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // Item to string conversion for selected display
  const itemToString = (item: Option | null) => {
    return item ? item.label : '';
  };

  // Find initial selected item
  const getInitialSelectedItem = () => {
    return options.find((option) => option.value === value) || null;
  };

  const stateReducer = useCallback((state, actionAndChanges) => {
    const { type, changes } = actionAndChanges;

    switch (type) {
      case useCombobox.stateChangeTypes.InputKeyDownEscape:
        // Prevent clearing input value on Escape
        return {
          ...changes,
          inputValue: state.inputValue, // Keep the current input value
        };
      default:
        return changes;
    }
  }, []);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
  } = useCombobox({
    stateReducer,
    selectedItem,
    items: inputItems,
    itemToString,
    initialSelectedItem: getInitialSelectedItem(),
    onInputValueChange: ({ inputValue = '', type }) => {
      // Track if user is actively typing (not from selection or reset)
      if (type === useCombobox.stateChangeTypes.InputChange) {
        hasUserTyped.current = true;
        const filteredItems = getFilteredItems(inputValue);
        setInputItems(filteredItems);
        setInputValue(inputValue);
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        hasUserTyped.current = false;
        setSelectedItem(selectedItem);
        onSelect?.(selectedItem.value);
      }
    },
    onIsOpenChange: ({ isOpen: newIsOpen, type }) => {
      // When dropdown closes and user was typing but didn't select anything,
      // reset input to show the currently selected item
      if (!newIsOpen && selectedItem) {
        if (
          type === useCombobox.stateChangeTypes.InputBlur ||
          type === useCombobox.stateChangeTypes.InputKeyDownEscape ||
          type === useCombobox.stateChangeTypes.InputKeyDownEnter
        ) {
          setInputValue(itemToString(selectedItem));
        }
      }

      if (!newIsOpen && !selectedItem) {
        setInputValue('');
      }
    },
  });

  const inputProps = getInputProps({
    required: props?.required,
  });

  // Update selected item when value prop changes
  useEffect(() => {
    const newSelectedItem = getInitialSelectedItem();
    if (newSelectedItem) {
      setInputValue(itemToString(newSelectedItem));
      setSelectedItem(newSelectedItem);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      setInputItems(options);
      hasUserTyped.current = false;
    }
  }, [isOpen, options]);

  return (
    <Container>
      <ComboboxContainer hasError={hasError}>
        <ComboboxWrapper>
          <ComboboxInput {...inputProps} {...(props || {})} />
          <ComboboxToggleButton
            data-testid="toggle-button"
            {...getToggleButtonProps()}
            aria-labelledby={inputProps.id}
          >
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </ComboboxToggleButton>
        </ComboboxWrapper>
        <ComboboxMenu {...getMenuProps()} isOpen={isOpen}>
          {isOpen && (
            <Fragment>
              {inputItems.length > 0 ? (
                inputItems.map((item, index) => (
                  <ComboboxItem
                    {...getItemProps({
                      item,
                      index,
                    })}
                    key={item.value}
                    isHighlighted={highlightedIndex === index}
                    isSelected={selectedItem === item}
                  >
                    {item.label}
                  </ComboboxItem>
                ))
              ) : (
                <ComboboxNoResults role="option" aria-disabled="true">
                  No Options
                </ComboboxNoResults>
              )}
            </Fragment>
          )}
        </ComboboxMenu>
      </ComboboxContainer>
      {helpText && (
        <ComboboxHelperText
          isError={hasError}
          role={hasError ? 'alert' : undefined}
          aria-live={hasError ? 'polite' : undefined}
        >
          {helpText}
        </ComboboxHelperText>
      )}
    </Container>
  );
};
